import React, { useState, useEffect } from 'react';
import { Sparkles, MessageSquare, Target, DollarSign, Terminal, ChevronRight, X } from 'lucide-react';

const TOUR_STEPS = [
  {
    icon: Sparkles,
    title: 'Welcome to ArtinSmartRealty 🚀',
    desc: 'The only Real Estate CRM powered by 7-language AI. Let\'s quickly show you around your new superpower.'
  },
  {
    icon: MessageSquare,
    title: 'AI WhatsApp & Telegram Bot',
    desc: 'Connect your accounts in Settings. The bot will automatically capture leads, qualify them, calculate ROI, and schedule viewings for you 24/7.'
  },
  {
    icon: Target,
    title: 'Realty Hunter 🎯',
    desc: 'Stop buying leads. Use the Hunter tool to extract premium investor contacts directly from LinkedIn and web pages.'
  },
  {
    icon: DollarSign,
    title: 'Transactions & Commissions',
    desc: 'Track your deals from MOU to Transfer. Manage agency commissions and split them seamlessly with your team.'
  },
  {
    icon: Terminal,
    title: 'Open API & Zapier',
    desc: 'Use the Developer API to automatically ingest leads from Facebook Ads, Zillow, or any custom form instantly.'
  }
];

const OnboardingTour = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Check if the user has already seen the tour
    const hasSeenTour = localStorage.getItem('artin_tour_completed');
    if (!hasSeenTour) {
      // Delay opening slightly for a better effect
      const timer = setTimeout(() => setIsOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleClose();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('artin_tour_completed', 'true');
  };

  if (!isOpen) return null;

  const Step = TOUR_STEPS[currentStep];
  const Icon = Step.icon;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
      <div className="bg-navy-900 border border-gold-500/30 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden relative animate-fade-in-up">
        
        {/* Glow effect */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-32 bg-gold-500/10 blur-[50px] rounded-full pointer-events-none" />

        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white z-10 transition-colors"
        >
          <X size={20} />
        </button>

        <div className="p-8 flex flex-col items-center text-center relative z-10">
          <div className="w-16 h-16 bg-gradient-to-br from-gold-400 to-gold-600 rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.4)] mb-6">
            <Icon className="w-8 h-8 text-navy-950" />
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-3">{Step.title}</h2>
          <p className="text-gray-400 leading-relaxed mb-8 min-h-[4rem]">
            {Step.desc}
          </p>

          {/* Progress indicators */}
          <div className="flex gap-2 mb-8">
            {TOUR_STEPS.map((_, idx) => (
              <div 
                key={idx} 
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === currentStep ? 'w-8 bg-gold-400' : 'w-2 bg-white/20'
                }`} 
              />
            ))}
          </div>

          <div className="flex w-full gap-3">
            <button 
              onClick={handleClose}
              className="flex-1 py-3 px-4 rounded-xl font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              Skip Tour
            </button>
            <button 
              onClick={handleNext}
              className="flex-[2] btn-gold flex items-center justify-center gap-2"
            >
              {currentStep === TOUR_STEPS.length - 1 ? 'Get Started' : 'Next'}
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
